import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { filterVisibleBanners } from '../../lib/dateVisibility';

/**
 * CarouselBannerBlock V4 - Avec gestion de visibilité par dates
 * 
 * ✅ DESIGN IDENTIQUE à banner_fixed_centered
 * ✅ Structure : Section + Container max-w-[95%] + Content centré
 * ✅ Hauteur min-h-[400px] (au lieu de h-[600px])
 * ✅ Image + Overlay + Contenu centré
 * ✅ Typography identique
 * ✅ Responsive identique
 * 
 * ✅ CAROUSEL FONCTIONNEL
 * ✅ Autoplay constant
 * ✅ Navigation flèches + points + swipe
 * ✅ Animation slide fluide
 * 
 * ✅ VISIBILITÉ PAR DATES (V4)
 * ✅ Filtre par start_date / end_date
 * ✅ Support ISO 8601 (UTC)
 * ✅ Fail-safe si dates invalides
 */
const CarouselBannerBlock = ({ block }) => {
  const { 
    banners = [], 
    interval = 5000,
    loop = true,
    animation = 'fade' // 'fade' ou 'slide'
  } = block.content || {};

  // ============================================================
  // FILTRE DE VISIBILITÉ V4
  // 1. Filtrer par is_active !== false
  // 2. Filtrer par dates (start_date / end_date)
  // ============================================================
  const activeBanners = React.useMemo(() => {
    // Étape 1: Filtrer par is_active
    const activeOnly = banners.filter(b => b.is_active !== false);
    
    // Étape 2: Filtrer par dates de visibilité
    const visibleBanners = filterVisibleBanners(activeOnly);
    
    // Log debug en développement
    if (process.env.NODE_ENV === 'development') {
      console.log(`🎠 [CarouselBanner] ${banners.length} banners total → ${activeOnly.length} actives → ${visibleBanners.length} visibles par date`);
    }
    
    return visibleBanners;
  }, [banners]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  // Navigation vers slide suivant (TOUJOURS +1)
  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => {
      if (loop) {
        return (prev + 1) % activeBanners.length;
      } else {
        return prev < activeBanners.length - 1 ? prev + 1 : prev;
      }
    });
  }, [activeBanners.length, loop]);

  // Navigation vers slide précédent (pour flèches uniquement)
  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => {
      if (loop) {
        return prev === 0 ? activeBanners.length - 1 : prev - 1;
      } else {
        return prev > 0 ? prev - 1 : prev;
      }
    });
  }, [activeBanners.length, loop]);

  // Navigation vers slide spécifique (pour points)
  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  // 🔥 AUTOPLAY CONSTANT
  useEffect(() => {
    if (activeBanners.length <= 1) return;

    const timer = setInterval(() => {
      setCurrentIndex((prev) => {
        if (loop) {
          return (prev + 1) % activeBanners.length;
        } else {
          return prev < activeBanners.length - 1 ? prev + 1 : 0;
        }
      });
    }, interval);

    return () => clearInterval(timer);
  }, [activeBanners.length, interval, loop]);

  // Swipe mobile
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const minSwipeDistance = 50;

    if (distance > minSwipeDistance) {
      goToNext();
    } else if (distance < -minSwipeDistance) {
      goToPrev();
    }

    setTouchStart(0);
    setTouchEnd(0);
  };

  // Si aucune bannière active, ne rien afficher
  if (activeBanners.length === 0) {
    if (process.env.NODE_ENV === 'development') {
      return (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
          <p className="text-sm text-yellow-700">
            ⚠️ Carousel vide : aucune bannière active
          </p>
        </div>
      );
    }
    return null;
  }

  return (
    // ============================================================
    // DESIGN SYSTEM CONTRACT: width = 100% du parent
    // Le layout parent contrôle la largeur, pas ce composant
    // ============================================================
    <div 
      className="carousel-banner-block"
      data-testid="carousel-banner-block"
      data-block-type="carousel_banner"
      style={{
        width: '100%',
        boxSizing: 'border-box'
      }}
    >
      <div 
        className="relative overflow-hidden rounded-xl"
        style={{
          width: '100%',
          minHeight: '400px',
          backgroundColor: '#1a1a2e'
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* 🔥 SLIDES - Même structure que banner_fixed_centered */}
        {activeBanners.map((banner, index) => {
          const isActive = index === currentIndex;
          
          return (
            <div
              key={banner.id || index}
              className={`
                absolute inset-0 transition-opacity duration-700
                ${animation === 'fade' 
                  ? (isActive ? 'opacity-100 z-10' : 'opacity-0 z-0')
                  : ''
                }
              `}
              style={{
                display: animation === 'slide' 
                  ? 'block' 
                  : (isActive ? 'block' : 'none'),
                // Animation slide : toujours de droite vers gauche
                transform: animation === 'slide'
                  ? `translateX(${100 * (index - currentIndex)}%)`
                  : 'none',
                transition: animation === 'slide'
                  ? 'transform 0.5s ease-in-out'
                  : 'none'
              }}
            >
              {/* 🔥 IMAGE DE FOND - Identique à banner_fixed_centered */}
              {banner.image && (
                <div
                  className="absolute inset-0 bg-cover bg-center"
                  style={{ backgroundImage: `url(${encodeURI(decodeURI(banner.image))})` }}
                  data-testid="carousel-banner-image"
                >
                  {/* Overlay assombrissant */}
                  <div 
                    className="absolute inset-0 bg-black" 
                    style={{ opacity: 0.4 }}
                  />
                </div>
              )}
              
              {/* Contenu centré - Identique à BannerBlock */}
              <div 
                className="relative flex items-center justify-center text-center"
                style={{
                  minHeight: '400px',
                  padding: '32px 16px'
                }}
              >
                <div style={{ maxWidth: '896px', width: '100%' }}>
                  {/* Titre avec typography identique */}
                  {banner.title && (
                    <h1 
                      className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold tracking-tight mb-4"
                      style={{ 
                        color: '#ffffff'
                      }}
                      data-testid="carousel-banner-title"
                    >
                      {banner.title}
                    </h1>
                  )}
                  
                  {/* Texte (si présent) */}
                  {banner.subtitle && (
                    <p 
                      className="text-sm sm:text-base md:text-lg lg:text-xl mb-6 sm:mb-8"
                      style={{ 
                        color: 'rgba(255, 255, 255, 0.9)'
                      }}
                      data-testid="carousel-banner-subtitle"
                    >
                      {banner.subtitle}
                    </p>
                  )}
                  
                  {/* Lien (si présent) - Wrapper pour navigation */}
                  {banner.link && (
                    <Link
                      to={banner.link}
                      className="inline-block px-6 sm:px-8 py-3 bg-white text-black text-sm sm:text-base font-medium rounded-lg hover:bg-gray-100 transition-colors"
                      data-testid="carousel-banner-link"
                    >
                      Découvrir
                    </Link>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {/* Navigation Flèches (Desktop) */}
        {activeBanners.length > 1 && (
          <>
            <button
              onClick={goToPrev}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-white/80 hover:bg-white p-3 rounded-full shadow-lg transition-all hover:scale-110 hidden md:flex items-center justify-center"
              aria-label="Bannière précédente"
              data-testid="carousel-prev-button"
            >
              <ChevronLeft className="h-6 w-6 text-gray-800" />
            </button>

            <button
              onClick={goToNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-white/80 hover:bg-white p-3 rounded-full shadow-lg transition-all hover:scale-110 hidden md:flex items-center justify-center"
              aria-label="Bannière suivante"
              data-testid="carousel-next-button"
            >
              <ChevronRight className="h-6 w-6 text-gray-800" />
            </button>
          </>
        )}

        {/* Points Indicateurs */}
        {activeBanners.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex gap-2">
            {activeBanners.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`
                  w-2 h-2 rounded-full transition-all
                  ${index === currentIndex 
                    ? 'bg-white w-8' 
                    : 'bg-white/50 hover:bg-white/75'
                  }
                `}
                aria-label={`Aller à la bannière ${index + 1}`}
                data-testid={`carousel-dot-${index}`}
              />
            ))}
          </div>
        )}

        {/* Debug Info (Dev) */}
        {process.env.NODE_ENV === 'development' && (
          <div className="absolute top-4 left-4 bg-blue-500 text-white px-2 py-1 rounded text-xs z-50">
            V4 DATE | Slide {currentIndex + 1}/{activeBanners.length}
          </div>
        )}
      </div>
    </div>
  );
};

export default CarouselBannerBlock;
